import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiAssetUrl } from "@/shared/api";
import { hasActiveTextSelection } from "@/shared/textSelection";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { useClipboardFeedback } from "@/shared/useClipboardFeedback";
import { ConfermaBonificoButton, isBonificoDaVerificare } from "./BonificoButton";
import { SpedizioneCell } from "./SpedizioneCell";
import { StatiCell } from "./StatiCell";
import { BADGE_TONI, coloriPaese, FORMA_BADGE, contorno, pieno, type StatoTone } from "./statoToni";
import { RiordinaSostButton } from "./Sostituzioni";
import { FORNITORE_LABEL } from "../fornitoreFiltro";
import { FORNITORI_CON_PORTALE } from "../portalOrdersApi";
import type { Order, OrderState } from "../types";
import { type AllServiceMailStatusDetail, orderHasAllServiceSku } from "../allserviceApi";
import { derivePickupMailStatusFromRaw } from "../pickupMailApi";
import { ritiroDelRicambio } from "../sostituzione";
import { ACTION_ICON_CLS, ActionButton } from "./ActionButton";
import { PagatoButton } from "./PagatoButton";
import { RientroMagazzinoButton, puoRientrare } from "./RientroMagazzino";
import { StampaZebraButton } from "./StampaZebraButton";
import type { OrderMarginRow } from "../economicSummaryApi";
import { MarginNetLine } from "./MarginNetLine";
import { CopyButton } from "./CopyButton";

export { CopyButton };
import { IconMail, IconReturn, IconSend, IconSwap } from "./icons";

// ponytail: CARRIER_DISPLAY_LABELS + normalizeCarrierLabel servivano solo al
// title del tasto "Genera LDV" dell'esplosione (rimossa, Piano 3 Task 3 —
// l'azione vive ora su /ordini/:ref, vedi ShippingSection.tsx). Rimossi come
// dead code.

/** Larghezze fisse delle 8 colonne della card lista (redesign F2 2026-07-14):
 *  checkbox · FOTO · ordine+prodotto · STATI · cliente · spedizione · conti ·
 *  azioni. Unica fonte: esportata cosi' l'header colonne (OrdiniPage) usa la
 *  STESSA stringa e resta allineato riga-su-riga (niente seconda copia da
 *  tenere in sync a mano). */
// minmax+fr: le colonne testo CRESCONO a riempire la riga su schermi larghi —
// niente vuoto morto a destra — restando ALLINEATE riga-su-riga. Rispetto al
// design precedente: FOTO 44→96px (item 17), nuova colonna STATI per i badge
// (item 19, niente piu' overflow-hidden), Azioni 188→216px per i tasti
// verticali con etichetta (item 1+21). Header (OrdiniColumnHeader) allineato da F1.
/** Risolve {source,label} per MarketplaceLogo dal raw.source Poleepo (channel
 *  e' vuoto). Fallback su order.marketplace per la modalita' mock.
 *  Esportata: riusata anche da OrderInfoPanel.tsx (mail), che ha un tipo
 *  ApiOrderItem diverso da Order — firma strutturale (solo i 2 campi usati)
 *  cosi' tipizza entrambi i caller senza cast. */
export function orderMarketplaceLogo(order: {
  raw?: { source?: unknown } | null;
  marketplace?: string | null;
}): { source: string; label: string } {
  const rawSource = order.raw?.source;
  const s = (typeof rawSource === "string" ? rawSource : "").toUpperCase();
  if (s.startsWith("AMAZON")) return { source: "amazon", label: "Amazon" };
  if (s.startsWith("LEROY")) return { source: "mirakl", label: "Leroy Merlin" };
  if (s.startsWith("MAISONS")) return { source: "mirakl", label: "Maisons du Monde" };
  if (s.startsWith("VENTE")) return { source: "mirakl", label: "Vente Unique" };
  if (s.startsWith("METRO")) return { source: "metro", label: "Metro" };
  if (s.startsWith("MANOMANO")) return { source: "manomano", label: "ManoMano" };
  if (s.startsWith("BRICO")) return { source: "mirakl", label: "Brico Bravo" };
  // 2026-10-22: Temu arriva da Poleepo come TEMU_IT (e TEMU dalla lane diretta).
  if (s.startsWith("TEMU")) return { source: "temu", label: "Temu" };
  // 2026-07-02 (Davide): ordini del sito → logo ufficiale WooCommerce.
  if (s.startsWith("WOO")) return { source: "woocommerce", label: "WooCommerce" };
  if (!s || s === "GENERIC")
    return { source: String(order.marketplace ?? "privato"), label: order.marketplace ?? "Altro" };
  // Canale nuovo (nessuna riga qui sopra): l'etichetta la fa la sorgente, resa
  // leggibile — "CDISCOUNT" → "Cdiscount", "BRICO_PRIVE" → "Brico Prive". Il
  // filtro canale della pagina Ordini si popola da qui, quindi un marketplace
  // nuovo compare da solo, scritto come un nome e non urlato in maiuscolo.
  return { source: "privato", label: leggibile(s) };
}

function leggibile(sorgente: string): string {
  return sorgente
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((parola) => parola[0]!.toUpperCase() + parola.slice(1))
    .join(" ");
}

/** Badge di STATO unificato (redesign W1, item 4): un solo componente, niente
 *  piu' 15 pill con classi copiate a mano.
 *
 *  Il colore arriva da ``BADGE_TONI`` come stile inline e non come classe
 *  Tailwind: `bg-[${hex}]` costruito a runtime non sopravvive al JIT, e una
 *  seconda mappa di classi da tenere allineata agli hex e' esattamente il tipo
 *  di doppione che questa revisione sta togliendo.
 *
 *  ``whitespace-nowrap``: l'etichetta sta su UNA riga (Davide 2026-09-01, con
 *  la foto di un "DA / ORDINARE" spezzato in due). La colonna STATI della
 *  lista non lo usa piu': le sue barre stanno in ``StatiCell``. */
export function StatoBadge({
  label,
  tone,
  title,
  testId,
}: {
  label: string;
  tone: StatoTone;
  title?: string;
  testId?: string;
}) {
  const colore = BADGE_TONI[tone];
  return (
    <span
      title={title}
      data-testid={testId}
      style={{ backgroundColor: colore, borderColor: colore }}
      className={`${FORMA_BADGE} text-white`}
    >
      {label}
    </span>
  );
}

/** Gruppo azioni della card lista: bonifico, Ordina, Pagato, Riordina,
 *  Invia LDV, Mail, resi e apri-pagina. Colonna Azioni fissa (redesign F2
 *  2026-07-14): ogni tasto e' un ActionButton verticale colorato per tono
 *  con micro-etichetta, stesso gating e stessi handler di prima — cambia
 *  solo la presentazione. */
export function CardActions({
  order,
  pickupMailBusy,
  returnBusy,
  onSendPickupMail,
  onCreateReturn,
  onShippingSaved,
  onOrderToSupplier,
  orderToSupplierBusy,
}: {
  order: Order;
  pickupMailBusy?: boolean;
  returnBusy?: boolean;
  onSendPickupMail?: (order: Order) => Promise<void>;
  onCreateReturn?: (orderRef: string, kind: "reso" | "sostituzione") => void;
  onShippingSaved?: () => void;
  onOrderToSupplier?: (order: Order) => void;
  orderToSupplierBusy?: boolean;
}) {
  const navigate = useNavigate();
  const bonificoPending = isBonificoDaVerificare(order);
  return (
    <>
      {/* Conferma bonifico (2026-07-02, Kery dal 2/11): ordini a bonifico in
          attesa. Primo della fila: e' l'azione che sblocca tutto il resto. */}
      {bonificoPending && <ConfermaBonificoButton order={order} onDone={onShippingSaved} compact />}

      {/* "Ordina" sul portale del fornitore (spec 2026-08-10): solo Ityhome e
          solo finche' non risulta gia' ordinato — il tasto sparisce invece di
          restare acceso e inerte. Apre lo stesso popup di conferma del batch:
          da qui non parte nessuna spesa senza che Davide veda cosa e quanto. */}
      {/* Mai prima del bonifico del cliente: come la lane ``*_invio``, che
          prende solo gli ordini pagati (29/10/2026). */}
      {FORNITORI_CON_PORTALE.includes(order.portaleFornitore ?? "") &&
        !order.supplierOrderRef &&
        !bonificoPending &&
        !!onOrderToSupplier && (
          <ActionButton
            tone="teal"
            icon={<IconSend className={ACTION_ICON_CLS} />}
            label="Ordina"
            busy={orderToSupplierBusy === true}
            disabled={orderToSupplierBusy === true}
            title={`Ordina ${order.id} sul portale ${order.portaleFornitore ?? ""}`}
            onClick={() => onOrderToSupplier(order)}
          />
        )}

      {/* «Pagato» dopo «Ordina»: prima si ordina, poi si paga. Mai su un «Da ordinare». */}
      {(order.paid === true ||
        (order.supplierUnpaid === true && order.supplierSendState !== "to_send")) && (
        <PagatoButton order={order} onDone={onShippingSaved} />
      )}

      {/* «Riordina» come sostituzione, numero S-<ref>, poi S2- (Davide 2026-09-24):
          ogni fornitore a cui OM sa ordinare, ordine originale gia' partito.
          Ha assorbito «Rispedisci AS»; si nasconde da solo dove non serve. */}
      <RiordinaSostButton order={order} onDone={onShippingSaved} compact />

      {/* Bottone "Invia LDV" sulla riga card (parity legacy). Stessa
          condizione canSendPickupMail di prima; stesso handler. */}
      {/* Gate gemello di ShippingSection: ``pickupMailMissing`` incluso, cosi'
          l'allarme "LDV mai inviata" porta con se' il tasto per rimediarlo. */}
      {orderHasAllServiceSku(order) === false &&
        (order.pickupReady === true ||
          order.merceProntaToday === true ||
          order.pickupMailMissing === true) &&
        !!onSendPickupMail && (
          <PickupMailButton
            order={order}
            busy={pickupMailBusy === true}
            onSend={onSendPickupMail}
          />
        )}

      {/* Zebra (Davide 2026-10-21): stampa la LDV gia' fatta sulla termica. */}
      {(order.trackingCode || order.ldvCode) && <StampaZebraButton order={order} />}

      {/* Bottone Mail: naviga al thread mail dell'ordine. Solo dove esiste
          una mail reale in outbox (Davide 2026-07-02). */}
      {order.hasMail && (
        <ActionButton
          tone="sky"
          icon={<IconMail className={ACTION_ICON_CLS} />}
          label="Mail"
          ariaLabel={`Mail ordine ${order.id}`}
          title={`Apri il thread mail dell'ordine ${order.id}`}
          onClick={(e) => {
            e.stopPropagation();
            void navigate(`/mail/${encodeURIComponent(order.id)}`);
          }}
        />
      )}

      {/* Bottoni Crea reso / Crea sostituzione: opzionali, visibili solo
          quando il parent (OrdiniPage) passa onCreateReturn. */}
      {onCreateReturn && (
        <>
          <ActionButton
            tone="rose"
            icon={<IconReturn className={ACTION_ICON_CLS} />}
            label="Reso"
            ariaLabel="Crea reso"
            title="Crea reso per questo ordine"
            disabled={returnBusy}
            onClick={(e) => {
              e.stopPropagation();
              onCreateReturn(order.id, "reso");
            }}
          />
          <ActionButton
            tone="violet"
            icon={<IconSwap className={ACTION_ICON_CLS} />}
            label="Sost."
            ariaLabel="Crea sostituzione"
            title="Crea sostituzione per questo ordine"
            disabled={returnBusy}
            onClick={(e) => {
              e.stopPropagation();
              onCreateReturn(order.id, "sostituzione");
            }}
          />
          {/* Rimetti in magazzino (Davide 2026-11-03): accanto a Reso, solo se la merce e' partita. */}
          {puoRientrare(order) && <RientroMagazzinoButton order={order} />}
        </>
      )}

      {/* ponytail: il tasto "Apri" non c'e' piu' (Davide 2026-09-01, «non
          serve»). Faceva esattamente quello che fa il clic sulla riga — aprire
          /ordini/:ref in una nuova scheda — e occupava un quinto slot che
          mandava le azioni a capo. Il clic-riga resta, invariato. */}
    </>
  );
}

/** Data+ora arrivo ordine: "14/07/2026 · 09:32". L'anno c'e' sempre (Davide
 *  28/09). ``null`` se il campo manca o non e' una data valida → la riga viene
 *  omessa (niente "--" di rumore). Serve anche la testata della pagina ordine. */
export function formatArrivo(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  const datePart = d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return `${datePart} · ${timePart}`;
}

/** Chip micro SKU copiabile (W1 item 2): click = copia QUEL singolo SKU con
 *  feedback inline. ``data-no-open`` cosi' il click non naviga la riga. */
export function SkuChip({ sku }: { sku: string }) {
  const { copied, copy } = useClipboardFeedback(1200);
  return (
    <button
      type="button"
      data-no-open
      data-testid="sku-chip"
      aria-label={`Copia SKU ${sku}`}
      title={copied ? "Copiato!" : `Copia SKU ${sku}`}
      onClick={(e) => {
        e.stopPropagation();
        void copy(sku);
      }}
      className={[
        "text-[9.5px] leading-none px-1 py-[2px] rounded border shrink-0 max-w-[120px] truncate transition-colors",
        copied
          ? "border-green text-green bg-card"
          : "border-line bg-paper-2 text-ink hover:border-line-2",
      ].join(" ")}
    >
      {copied ? "Copiato!" : sku}
    </button>
  );
}

/** Riga SKU della card (W1 item 2): un chip per item. Oltre 3 SKU mostra i
 *  primi 3 + "+N" (title con gli altri) per non far crescere la card. Una
 *  riga sola (``flex-nowrap`` + ``overflow-hidden``). */
export function SkuChips({
  items,
  totalQuantity,
}: {
  items: NonNullable<Order["items"]>;
  totalQuantity?: number | null;
}) {
  const skus = items.map((it) => it.sku).filter((s): s is string => Boolean(s));
  if (skus.length === 0) return null;
  const shown = skus.slice(0, 3);
  const rest = skus.slice(3);
  // "×N" arancione accanto allo SKU (Davide 2026-07-14): SOLO quando l'ordine
  // ha UN solo SKU distinto — con SKU diversi il totale complessivo non dice
  // nulla di utile (le quantita' stanno nelle righe della pagina ordine).
  const distinct = new Set(skus).size;
  const qty = totalQuantity ?? 0;
  const showQty = distinct === 1 && qty > 1;
  return (
    <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
      {shown.map((sku, i) => (
        <SkuChip key={`${sku}-${i}`} sku={sku} />
      ))}
      {rest.length > 0 && (
        <span
          title={rest.join(", ")}
          className="text-[9.5px] leading-none px-1 py-[2px] rounded border border-line bg-paper-2 text-ink shrink-0"
        >
          +{rest.length}
        </span>
      )}
      {showQty && (
        <span
          data-testid="sku-qty"
          title={`${qty} pezzi dello stesso articolo`}
          className="text-[10.5px] font-semibold leading-none text-orange-600 shrink-0"
        >
          ×{qty}
        </span>
      )}
    </div>
  );
}

/** Riga contatti cliente (W1 item 6): telefono come link ``tel:`` + email
 *  copiabile con feedback. Ciascuno omesso se mancante; entrambi
 *  ``data-no-open`` (il click non naviga la riga). */
export function CustomerContactRow({ phone, email }: { phone?: string; email?: string }) {
  const { copied, copy } = useClipboardFeedback(1200);
  const tel = (phone ?? "").trim();
  const mail = (email ?? "").trim();
  if (!tel && !mail) return null;
  return (
    <div className="flex items-center gap-2 min-w-0">
      {tel && (
        <a
          href={`tel:${tel.replace(/\s+/g, "")}`}
          data-no-open
          onClick={(e) => e.stopPropagation()}
          className="text-[10.5px] text-accent shrink-0 hover:underline"
        >
          {tel}
        </a>
      )}
      {mail && (
        <button
          type="button"
          data-no-open
          title={copied ? "Copiato!" : `Copia email ${mail}`}
          aria-label={`Copia email ${mail}`}
          onClick={(e) => {
            e.stopPropagation();
            void copy(mail);
          }}
          className={[
            "text-[10.5px] truncate min-w-0 text-left transition-colors",
            copied ? "text-green" : "text-ink",
          ].join(" ")}
        >
          {copied ? "Copiato!" : mail}
        </button>
      )}
    </div>
  );
}

interface ThumbnailProps {
  src: string | null | undefined;
  alt: string;
  /** Classe di misura: 96px in lista desktop, 64px sul telefono. */
  misura?: string;
}

function Thumbnail({ src, alt, misura = "w-24 h-24" }: ThumbnailProps) {
  const [errored, setErrored] = useState(false);
  // 96px (redesign F2, item 17): foto grande quasi a piena altezza della card.
  // Poco bordo (border-line/50) per non incorniciarla troppo.
  const baseCls = `${misura} self-center rounded-lg border border-line/60 shrink-0 overflow-hidden`;
  if (src && !errored) {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
        className={`${baseCls} object-cover bg-paper`}
      />
    );
  }
  return (
    // Senza foto: un pacco su fondo carta (la scritta «IMG» sembrava un errore).
    <div
      className={`${baseCls} inline-grid place-items-center bg-paper-2 text-ink-3`}
      aria-hidden="true"
    >
      <svg
        className="h-1/3 w-1/3 fill-none stroke-current stroke-[1.4] [stroke-linecap:round] [stroke-linejoin:round]"
        viewBox="0 0 24 24"
      >
        <path d="M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8" />
      </svg>
    </div>
  );
}

/** Mini-foto per la griglia 2x2 multi-item (W1 item 8): cella ~46px, lazy.
 *  Su errore degrada a un fondo carta pieno, come Thumbnail senza foto. */
function MiniThumb({ src, alt }: { src: string; alt: string }) {
  const [errored, setErrored] = useState(false);
  const base = "w-full h-full rounded-sm border border-line/50 overflow-hidden";
  if (errored) {
    return <div className={`${base} bg-paper-2`} aria-hidden="true" />;
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setErrored(true)}
      data-testid="card-mini-thumb"
      className={`${base} object-cover bg-paper`}
    />
  );
}

/** Cella foto della card (W1 item 8): un solo item → foto 96px come prima;
 *  piu' item con id (foto per-riga disponibili) → griglia 2x2 delle prime 4
 *  (endpoint per-item /api/orders/items/{id}/thumb). Oltre 4: 3 foto + cella
 *  overlay "+N". */
export function PhotoCell({ order, misura = "w-24 h-24" }: { order: Order; misura?: string }) {
  const withId = (order.items ?? []).filter(
    (it): it is NonNullable<Order["items"]>[number] & { id: number } => it.id != null,
  );
  if (withId.length <= 1) {
    return <Thumbnail src={order.thumbnail} alt={order.productName} misura={misura} />;
  }
  // >4 → mostra 3 foto e riserva l'ultima cella all'overlay "+N".
  const shown = withId.length > 4 ? withId.slice(0, 3) : withId.slice(0, 4);
  const extra = withId.length - shown.length;
  return (
    <div className={`${misura} self-center grid grid-cols-2 grid-rows-2 gap-[2px] shrink-0`}>
      {shown.map((it) => (
        <MiniThumb
          key={it.id}
          src={apiAssetUrl(`/api/orders/items/${it.id}/thumb`)}
          alt={it.title}
        />
      ))}
      {extra > 0 && (
        <div
          aria-label={`+${extra} altre foto`}
          className="rounded-sm bg-slate-800/70 text-white text-[10px] font-medium inline-grid place-items-center"
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

interface OrderCardProps {
  order: Order;
  isSelected: boolean;
  pickupMailBusy?: boolean;
  onToggleSelect: (id: string) => void;
  /** Prepara la mail di spedizione (LDV allegata) per gli ordini "pronto al ritiro". */
  onSendPickupMail?: (order: Order) => Promise<void>;
  /** Chiamato dopo aver salvato i dati spedizione "al volo" — il parent
   *  (OrdiniPage) fa refresh della lista per riflettere colli/peso. */
  onShippingSaved?: () => void;
  /** Dialog reso/sostituzione; senza, i due bottoni non si vedono. Riceve `order.id`. */
  onCreateReturn?: (orderRef: string, kind: "reso" | "sostituzione") => void;
  /** Quando true, i bottoni reso/sostituzione vengono disabilitati (richiesta
   *  al backend in corso per questo ordine). */
  returnBusy?: boolean;
  /** Apre il popup di conferma per ordinare questo ordine sul portale del
   *  fornitore (oggi solo Ityhome). Assente = tasto "Ordina" non renderizzato. */
  onOrderToSupplier?: (order: Order) => void;
  /** Vero mentre una preview/un ordine portale è in volo: il tasto "Ordina" si
   *  spegne su TUTTE le card, perché sul portale il carrello è uno solo. */
  orderToSupplierBusy?: boolean;
  /** Margine netto dal motore economics (parametri AM), fetchato in batch da
   *  OrdiniPage (useOrderMargins). Assente = non ancora caricato o marketplace
   *  non coperto: la card mostra solo totale/costo come prima. */
  marginRow?: OrderMarginRow | null;
}

/** Stato ordine → StatoBadge (W1 item 4). "In corso" (era "In lavorazione"):
 *  etichetta corta per stare su una riga sui ref Amazon lunghi. */
const STATE_STATO: Record<OrderState, { label: string; tone: StatoTone }> = {
  nuovo: { label: "Nuovo", tone: "nuovo" },
  in_lavorazione: { label: "In corso", tone: "in-corso" },
  pronto: { label: "Pronto", tone: "pronto" },
  spedito: { label: "Spedito", tone: "spedito" },
  annullato: { label: "Annullato", tone: "annullato" },
};

/** Badge fornitore: stessa palette Material degli stati, ma tinte SUE — il
 *  fornitore e' identita', non stato, e vale il codice colore che Davide ha in
 *  testa (Itamoby caldo, Ityhome blu, AllService verde, Cosma ambra). Nessuna
 *  di queste coincide con un tono di stato: ``badgeToni.test.ts`` verifica che
 *  i 26 colori della card siano tutti diversi fra loro.
 *
 *  Il pallino colorato su fondo bianco e' sparito il 2026-09-01: era l'unico
 *  badge "spento" della card. */
export const FORNITORE_TONI: Record<string, string> = {
  itamoby: "#a1471a", // Deep Orange scuro — la terracotta di sempre
  ityhome: "#185abc", // Blue 900
  allservice: "#0d652d", // Green 900
  cosma: "#7c5800", // Yellow 900 scuro
  // I quattro fornitori che il PIM sa e ``SupplierKind`` no (2026-09-01).
  // Toni e contrasto col bianco verificati da ``badgeToni.test.ts``.
  bizzotto: "#6a1b9a", // Purple 800
  mauro_ferretti: "#4e342e", // Brown 800
  web_furniture: "#006064", // Cyan 900
  oyat: "#37474f", // Blue Grey 800
  aurora: "#c2185b", // Pink 700 — Aurora Bedding (2026-09-14)
  unknown: "#6b7175", // Grey 600
};

/** Badge NOME fornitore, SEMPRE in card (Davide 2026-07-06). Ora l'UNICO
 *  badge identita'-fornitore in card (Piano 3 Task 4, 2026-07-13): il badge
 *  storico FornitoreBadge (mostrava order.fornitore.name — spesso lo stesso
 *  testo, es. "ALL SERVICE") duplicava questo e sparisce (bug "badge
 *  fornitore duplicato" da screenshot Davide). Kind ignoto → n/d onesto. */
function SupplierNameBadge({ kind }: { kind: string }) {
  const label = FORNITORE_LABEL[kind] ?? FORNITORE_LABEL.unknown;
  const colore = FORNITORE_TONI[kind] ?? FORNITORE_TONI.unknown;
  return (
    <span
      data-testid="supplier-name-badge"
      title="Fornitore"
      style={{ backgroundColor: colore }}
      className="inline-flex items-center whitespace-nowrap px-2 py-px rounded-[4px] text-[10px] font-semibold uppercase tracking-[0.05em] text-white shrink-0"
    >
      {label}
    </span>
  );
}

/** Fase A multi-fornitore (2026-07-06): per gli ordini dropshipping MISTI
 *  (item di fornitori diversi) un solo badge nascondeva gli altri. Qui:
 *  badge "MISTO" (violet, tooltip = chi fornisce cosa) + un badge nome per
 *  OGNI fornitore riconosciuto. Ordine non misto: badge singolo classico. */
export function SupplierBadges({ order }: { order: Order }) {
  const itemKinds = order.itemSupplierKinds ?? {};
  if (order.isMixed !== true) {
    // Kind ignoto: lo chiede al PIM (368 SKU venduti mostravano "Forn. n/d"
    // con la risposta gia' in mano). ADDITIVO: dove un kind c'e' comanda lui,
    // o un ordine AS coi pezzi ItyHome smetterebbe di dirsi AS.
    const kind =
      order.fornitore.kind !== "unknown"
        ? order.fornitore.kind
        : (Object.values(order.fornitoriMerce ?? {})[0] ?? "unknown");
    return <SupplierNameBadge kind={kind} />;
  }
  const distinct: string[] = [];
  for (const kind of Object.values(itemKinds)) {
    if (kind !== "unknown" && !distinct.includes(kind)) distinct.push(kind);
  }
  const breakdown = Object.entries(itemKinds)
    .map(([sku, kind]) => `${sku} → ${FORNITORE_LABEL[kind] ?? kind}`)
    .join("\n");
  return (
    <>
      <span
        data-testid="mixed-supplier-badge"
        title={`Ordine con fornitori diversi:\n${breakdown}`}
        className="inline-flex items-center gap-1 px-2 py-px rounded-full text-[10px] font-semibold uppercase tracking-[0.04em] shrink-0 bg-violet text-white"
      >
        Misto
      </span>
      {distinct.map((kind) => (
        <SupplierNameBadge key={kind} kind={kind} />
      ))}
    </>
  );
}

export function Badge({ state }: { state: OrderState }) {
  const { label, tone } = STATE_STATO[state];
  return <StatoBadge label={label} tone={tone} />;
}

/** Chip deadline spedizione marketplace (R3, Davide 2026-07-03): "entro
 *  gg/mm" accanto a SPEDIZIONE. Colore per urgenza (non solo colore:
 *  l'etichetta cambia): rosso pieno se scaduta, rosso soft se entro 24h,
 *  ambra entro 48h, neutro oltre. Nascosto su ordini spediti/annullati
 *  (deadline non piu' azionabile). */
export function ShipDeadlineChip({
  deadline,
  state,
}: {
  deadline?: string | null;
  state: OrderState;
}) {
  if (!deadline || state === "spedito" || state === "annullato") return null;
  const due = new Date(deadline);
  if (Number.isNaN(due.getTime())) return null;
  const hoursLeft = (due.getTime() - Date.now()) / 3_600_000;
  const dayMonth = due.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  const tooltip = `Deadline di spedizione del marketplace: ${due.toLocaleString("it-IT")}`;
  // Nello stile dei badge di stato (Davide 2026-09-23): PIENA quando e'
  // passata, a CONTORNO quando stringe (rossa entro 24h, arancio entro 48h),
  // neutra oltre. L'etichetta cambia insieme al colore: mai colore da solo.
  const colore =
    hoursLeft < 0
      ? pieno("scaduta")
      : hoursLeft < 24
        ? contorno("scaduta")
        : hoursLeft < 48
          ? contorno("in-corso")
          : undefined;
  return (
    <span
      title={tooltip}
      data-testid="ship-deadline-chip"
      style={colore}
      className={`${FORMA_BADGE} tabular-nums ${colore ? "" : "border-line-2 bg-card text-ink"}`}
    >
      {hoursLeft < 0 ? `oltre ${dayMonth}` : `entro ${dayMonth}`}
    </span>
  );
}

/** "La spedizione scade OGGI, o e' gia' scaduta?" — confronto per GIORNO in
 *  fuso Europe/Rome, non a ore: la deadline marketplace puo' avere un'ora
 *  precisa (Amazon LatestShipDate, Mirakl) e Poleepo marca `Z` l'ora italiana,
 *  quindi un confronto a ore sbaglierebbe il giorno. Usato per il bordo rosso
 *  "spedire oggi" della card (item 14).
 *
 *  🔴 Una deadline passata NON smette di essere urgente a mezzanotte: prima qui
 *  c'era un `=== oggi` e il giorno dopo la card perdeva il bordo rosso proprio
 *  quando l'ordine era in RITARDO (Davide 2026-09-22). Lo scaduto e' il caso
 *  piu' grave, non l'assenza di un caso: ha un livello suo, piu' marcato.
 *  Le date "en-CA" sono `YYYY-MM-DD`, quindi confrontarle come stringhe e'
 *  confrontarle in ordine cronologico. */
export function shipDeadlineUrgency(
  deadline: string | null | undefined,
  state: OrderState,
): "oggi" | "scaduta" | null {
  if (!deadline || state === "spedito" || state === "annullato") return null;
  const due = new Date(deadline);
  if (Number.isNaN(due.getTime())) return null;
  const romeDay = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "Europe/Rome" });
  const oggi = romeDay(new Date());
  const giorno = romeDay(due);
  if (giorno === oggi) return "oggi";
  return giorno < oggi ? "scaduta" : null;
}

/** Badge mail spedizione Itamoby/Ityhome (Davide 2026-07-03) — stato da
 *  mostrare, o null se non pertinente. Visibile solo quando il flusso
 *  pickup e' rilevante (mail inviata/pending, o merce pronta in attesa):
 *  "Mai inviata" su ordini appena arrivati sarebbe rumore, la pickup
 *  mail si manda solo DOPO la mail "merce pronta" del fornitore. */
export function pickupMailStatusFor(order: Order): AllServiceMailStatusDetail | null {
  const kind = order.fornitore.kind;
  if (kind !== "itamoby" && kind !== "ityhome") return null;
  const status = derivePickupMailStatusFromRaw(order.raw, kind);
  // pickupMailMissing: anche gli spediti con mail mai partita mostrano lo
  // stato "Mai inviata" + bottone "Verifica IMAP" (fix 2026-07-16).
  const visible =
    status.state !== "never_sent" ||
    order.pickupReady === true ||
    order.merceProntaToday === true ||
    order.pickupMailMissing === true;
  return visible ? status : null;
}

/** Il numero d'ordine del FORNITORE, scritto come il NOSTRO (Davide
 *  2026-08-14). Erano pill bordate: due numeri che sono la stessa cosa vista
 *  dai due lati si leggevano come cose di specie diversa, e l'occhio doveva
 *  ricomporli. Stessa famiglia, stesso corpo, stesso peso del numero nostro —
 *  a distinguerli basta il "N°" (il grigio e' sparito il 28/09: testo nero).
 *
 *  Il ``title`` porta la semantica che il colore non porta piu': per Cosma
 *  (nessuna API di conferma, arriva per mail) il numero E' la prova che
 *  l'ordine e' stato accettato. */
function SupplierOrderNumber({ text, title }: { text: string; title: string }) {
  return (
    <span
      data-testid="supplier-order-number"
      title={title}
      className="shrink-0 text-[12px] font-medium text-ink tracking-[-0.01em] cursor-help"
    >
      {text}
    </span>
  );
}

// ponytail: CosmaConfirmedBadge e SupplierOrderRefBadge (due pill che
// differivano per il solo testo) sono diventate due chiamate a
// ``SupplierOrderNumber`` con etichetta e tooltip diversi — la differenza sta
// nei dati, non in due componenti da tenere allineati a mano.

// ponytail: FornitoreBadge (mostrava order.fornitore.name grezzo) rimossa —
// duplicava SupplierNameBadge/SupplierBadges (bug "ALL SERVICE" ×2 dallo
// screenshot Davide 2026-07-13). Nessun altro caller nel repo (grep verificato).

/** Editor "al volo" dei dati spedizione: Colli + peso + dimensioni.
 *  Pre-compila dai dati gia' salvati (order.packageDimensions) oppure dal
 *  listino fornitore (order.suggestedPackages — per Cosma il peso viene
 *  dal Google Sheet). Per la LDV conta soprattutto il peso: le dimensioni
 *  possono restare 0 (regola operativa Davide). Un solo collo: copre il
 *  caso BSCom tipico; il backend accetta comunque una lista. */
/** Opzioni del click "Genera LDV": il N° Colli WYSIWYG, il flag force e —
 *  bug 2026-06-16 — le dimensioni editate (con use_pallet) da persistere
 *  prima di generare, cosi' la LDV usa le misure NUOVE e non quelle vecchie. */
export type GenerateLabelClickOpts = {
  packagesCount?: number;
  force?: boolean;
  packageDimensions?: Array<{ kg: number; l: number; a: number; p: number }>;
  usePallet?: boolean;
  /** Corriere scelto nel dropdown dell'editor spedizione (codice CarrierCode). */
  carrier?: string;
};

/** Tasto "Invia LDV" della colonna Azioni (redesign F2): ActionButton
 *  viola/'LDV' con logica 3-stati busy/alreadySent/default. La variante
 *  "panel" e' stata rimossa (dead code: nessun chiamante). Gli aria-label
 *  restano gli stessi (i test by-role li cercano). */
export function PickupMailButton({
  order,
  busy,
  onSend,
}: {
  order: Order;
  busy: boolean;
  onSend: (o: Order) => Promise<void>;
}) {
  const alreadySent = order.pickupMailSent === true;
  const ricambio = ritiroDelRicambio(order);
  const ariaLabel = busy
    ? "Invio LDV in corso…"
    : alreadySent
      ? "Reinvia mail LDV (gia' inviata)"
      : ricambio
        ? "Invia LDV del ricambio"
        : "Invia LDV";
  // Il click PREPARA la mail (popup SMTP): l'invio resta un secondo gesto esplicito.
  const title = busy
    ? "Preparazione della mail di spedizione in corso…"
    : alreadySent
      ? "Mail LDV gia' inviata — clicca per reinviare se necessario"
      : ricambio
        ? "Prepara la mail di ritiro del ricambio, con l'etichetta della sostituzione allegata (mai la LDV originale), da rileggere prima dell'invio"
        : "Prepara la mail di spedizione al fornitore, con la LDV allegata, da rileggere prima dell'invio";

  return (
    <ActionButton
      tone={alreadySent ? "emerald" : "purple"}
      icon={
        <IconSend className="w-[15px] h-[15px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
      }
      label="LDV"
      ariaLabel={ariaLabel}
      title={title}
      busy={busy}
      onClick={(e) => {
        e.stopPropagation();
        void onSend(order);
      }}
    />
  );
}

export function OrderCard({
  order,
  isSelected,
  pickupMailBusy,
  onToggleSelect,
  onSendPickupMail,
  onShippingSaved,
  onCreateReturn,
  returnBusy,
  marginRow,
  onOrderToSupplier,
  orderToSupplierBusy,
}: OrderCardProps) {
  const formattedTotal = `€ ${order.total.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
  // Data+ora arrivo ordine (item 5): omessa se il campo manca/non valido.
  const arrivo = formatArrivo(order.createdAtPoleepo);
  // Bordo giallo bonifico: vince su inWarehouse (il pagamento e' il gate che
  // blocca tutto il flusso a valle, magazzino compreso).
  const bonificoPending = isBonificoDaVerificare(order);
  // Bordo rosso "spedire oggi" (item 14): sotto il giallo-bonifico (un ordine
  // non pagato non e' spedibile), sopra teal-magazzino e accento-selezione.
  // Due livelli: `oggi` e `scaduta`, che e' piu' rossa (Davide 2026-09-22).
  const shipUrgency = shipDeadlineUrgency(order.shipDeadline, order.state);
  // Chi c'e' nella colonna STATI, e in che ordine, lo decide ``statiDellOrdine``.

  return (
    <article
      data-testid={`order-card-${order.id}`}
      data-bonifico-pending={bonificoPending || undefined}
      data-ship-today={shipUrgency === "oggi" || undefined}
      data-ship-late={shipUrgency === "scaduta" || undefined}
      className={[
        // Hover: ombra, mai transform (chiuderebbe nella card i dialog `fixed`).
        "bg-card border rounded-xl mb-2 transition-[border-color,box-shadow] duration-200 ease-out overflow-hidden relative hover:shadow-[0_10px_28px_-14px_rgba(31,27,22,0.28)]",
        bonificoPending
          ? "border-[#EAB308] [background:color-mix(in_oklab,#EAB308_7%,rgb(var(--om-card)))] shadow-[0_0_0_2px_rgba(234,179,8,0.4)]"
          : shipUrgency === "scaduta"
            ? // Deadline PASSATA: stesso rosso, piu' carico — si vede da lontano
              // che quell'ordine e' in ritardo, non solo in scadenza.
              "border-red [background:color-mix(in_oklab,#B5443A_18%,rgb(var(--om-card)))] shadow-[0_0_0_3px_rgba(181,68,58,0.8)]"
            : shipUrgency === "oggi"
              ? // Item 14 (Davide): ordini da spedire OGGI, massima evidenza in lista.
                "border-red [background:color-mix(in_oklab,#B5443A_8%,rgb(var(--om-card)))] shadow-[0_0_0_2px_rgba(181,68,58,0.45)]"
              : order.inWarehouse
                ? // Teal (Davide 2026-07-06): l'amber precedente era quasi
                  // identico al giallo bonifico — merce a stock BS COM (mirror
                  // MATECPRO) deve saltare all'occhio come categoria SUA.
                  "border-teal [background:color-mix(in_oklab,#4A8A8A_9%,rgb(var(--om-card)))] shadow-[0_0_0_2px_rgba(74,138,138,0.35)]"
                : isSelected
                  ? "border-accent [background:color-mix(in_oklab,#C96442_4%,rgb(var(--om-card)))]"
                  : "border-line hover:border-line-2",
        // Merce BS COM (Davide 2026-10-21): la striscia resta anche sotto il
        // rosso «spedire oggi» e il giallo bonifico, che vincono sul fondo.
        order.inWarehouse ? "border-l-[6px] border-l-teal" : "",
      ].join(" ")}
    >
      {/* Main row — colonne FISSE (redesign F2 2026-07-14):
          checkbox · FOTO · ordine+prodotto · STATI · cliente · spedizione ·
          conti · azioni. Il corpo naviga a /ordini/:ref, i tasti sono
          ActionButton verticali nell'ultima colonna. py ridotto: la foto 96px
          riempie quasi tutta l'altezza della card. */}
      <div
        className="griglia-ordini con-divisori items-stretch py-3 px-4 max-md:!flex max-md:flex-col max-md:items-stretch max-md:gap-2"
        onClick={(e) => {
          // Non navigare se il click e' su checkbox, azione, copy o link.
          const target = e.target as HTMLElement;
          if (target.closest("[data-no-open]") || target.closest("button") || target.closest("a"))
            return;
          // L'utente stava selezionando testo (per copiarlo): niente navigazione.
          if (hasActiveTextSelection()) return;
          // Item 3 (Davide): la pagina ordine si apre in una NUOVA scheda.
          // window.open dentro l'handler = user gesture, nessun popup blocker.
          window.open(`/ordini/${encodeURIComponent(order.id)}`, "_blank", "noopener");
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          // Stessa guardia del click: la colonna Azioni ora vive DENTRO la riga
          // (era un sibling nel design precedente), quindi la digitazione in un
          // dialog annidato (es. RiordinaSostDialog, data-no-open) bubble-a fin
          // qui — senza guardia lo spazio nel campo "cosa" veniva mangiato dal
          // preventDefault sotto (bug scoperto in test, Piano 3 Task 3).
          const target = e.target as HTMLElement;
          if (target.closest("[data-no-open]") || target.closest("button") || target.closest("a"))
            return;
          e.preventDefault();
          window.open(`/ordini/${encodeURIComponent(order.id)}`, "_blank", "noopener");
        }}
        aria-label={`Ordine ${order.id}`}
      >
        {/* Checkbox */}
        <button
          type="button"
          data-no-open
          aria-label={`Seleziona ordine ${order.id}`}
          aria-pressed={isSelected}
          className={[
            "w-[18px] h-[18px] self-center border-[1.5px] rounded-[4px] inline-grid place-items-center shrink-0 cursor-pointer transition-all duration-[120ms]",
            isSelected ? "bg-accent border-accent" : "bg-card border-line-2 hover:border-ink-4",
          ].join(" ")}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(order.id);
          }}
          data-testid={`checkbox-${order.id}`}
        >
          <svg
            className={[
              "w-3 h-3 fill-none stroke-white stroke-[3] [stroke-linecap:round] [stroke-linejoin:round] transition-opacity",
              isSelected ? "opacity-100" : "opacity-0",
            ].join(" ")}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </button>

        {/* Foto prodotto (item 8): 96px singola, o griglia 2x2 se piu' item
            hanno foto per-riga disponibili. */}
        <PhotoCell order={order} />

        {/* Colonna ORDINE + PRODOTTO: data arrivo (item 5) + riga1 logo/ref/
            copy + riga2 titolo + riga3 fornitore e SKU copiabili (item 2). I
            badge di STATO vivono nella colonna STATI dedicata (item 19). */}
        <div
          data-testid={`order-prodotto-${order.id}`}
          className="min-w-0 flex flex-col justify-center gap-[4px]"
        >
          {arrivo && (
            <div
              data-testid="order-arrivo"
              className="text-[11px] leading-none tabular-nums text-ink"
            >
              {arrivo}
            </div>
          )}
          {/* Riga IDENTITA' (Davide 2026-08-13): il NOSTRO numero e il LORO,
              appaiati. Sono la stessa cosa vista dai due lati — tenerli a
              distanza (il n° fornitore stava in fondo alla colonna STATI, dopo
              8 badge) costringeva a cercarlo per collegarli. Il badge col NOME
              del fornitore sta invece sulla riga degli SKU, sotto. */}
          <div
            data-testid={`order-identity-${order.id}`}
            className="flex items-center gap-1.5 overflow-hidden max-h-6"
          >
            <MarketplaceLogo {...orderMarketplaceLogo(order)} size={18} className="shrink-0" />
            <span className="shrink-0 font-display text-[14px] font-semibold text-ink tracking-[-0.01em] min-[1400px]:text-[15px]">
              {order.id}
            </span>
            <CopyButton text={order.id} label="Copia ID ordine" />
            {order.supplierOrderRef &&
              (order.fornitore.kind === "cosma" ? (
                <SupplierOrderNumber
                  text={`COSMA · N° ${order.supplierOrderRef}`}
                  title={`Ordine inviato a COSMA e confermato (n° ${order.supplierOrderRef})`}
                />
              ) : (
                <SupplierOrderNumber
                  text={`N° ${order.supplierOrderRef}`}
                  title={`Numero ordine fornitore: ${order.supplierOrderRef}`}
                />
              ))}
          </div>

          <div className="text-[14px] font-semibold leading-[1.3] text-ink truncate tracking-[-0.005em]">
            {order.productName}
          </div>

          {/* Chi fornisce, accanto allo SKU (Davide 2026-09-01). Il fornitore
              e' una proprieta' della MERCE, non uno stato dell'ordine: fra i
              badge di stato era il primo chip di una colonna che parla d'altro,
              e per sapere chi spedisce quello SKU l'occhio doveva attraversare
              la card. */}
          <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
            <SupplierBadges order={order} />
            {order.items && order.items.length > 0 && (
              <SkuChips items={order.items} totalQuantity={order.totalQuantity} />
            )}
          </div>
        </div>

        {/* Colonna STATI: un fatto per riga (Davide 2026-09-23) — vedi StatiCell. */}
        <StatiCell order={order} />

        {/* Colonna CLIENTE: 2 righe come il mockup — nome bold, indirizzo
            sotto. Niente icona, niente separatori verticali, niente label
            (l'etichetta vive nell'header sopra la lista). */}
        <div className="min-w-0 flex flex-col justify-center gap-[2px]">
          <div className="text-[13.5px] font-semibold text-ink truncate">{order.customer.name}</div>
          <div className="flex min-w-0 items-center gap-1 text-[12px] text-ink">
            {/* Paese nello stile del chip corriere (Davide 2026-09-28): vuoto = IT,
                come `spedizione_estera` nel backend. */}
            <span
              title="Paese di spedizione"
              style={coloriPaese(order.customer.country || "IT")}
              className={FORMA_BADGE}
            >
              {(order.customer.country || "IT").toUpperCase()}
            </span>
            <span className="truncate">
              {order.customer.address} · {order.customer.city}
            </span>
          </div>
          {/* Riga3 (item 6): telefono cliccabile + email copiabile. */}
          <CustomerContactRow phone={order.customer.phone} email={order.customer.email} />
        </div>

        {/* Colonna SPEDIZIONE: corriere e colli, codice copiabile, stato del
            corriere e, se c'e', il tracking della SOSTITUZIONE. */}
        <SpedizioneCell
          order={order}
          scadenza={<ShipDeadlineChip deadline={order.shipDeadline} state={order.state} />}
        />

        {/* Colonna CONTI: totale serif, costo, margine netto. Mai `truncate`
            (Davide 2026-09-23: «in certi casi vengono tagliati i numeri»): sul
            portatile la colonna e' da 130px e il costo a quattro cifre finiva
            coi puntini. Ogni importo e' un blocco che non si spezza, e se non
            c'e' posto si va a capo FRA un importo e l'altro, mai dentro. */}
        <div
          data-testid={`order-conti-${order.id}`}
          className="min-w-0 flex flex-col items-end justify-center gap-px text-right"
        >
          <div className="whitespace-nowrap font-display text-[19px] font-semibold leading-tight tabular-nums tracking-[-0.02em] text-ink">
            {formattedTotal}
          </div>
          {order.costo > 0 && (
            <div className="flex flex-wrap justify-end gap-x-1 text-[11.5px] leading-snug text-ink">
              <span className="whitespace-nowrap">
                costo € {order.costo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
              </span>
              {order.margin && (
                <span className="whitespace-nowrap before:mr-1 before:content-['·']">
                  {order.margin}
                </span>
              )}
            </div>
          )}
          <MarginNetLine row={marginRow} />
        </div>

        {/* Colonna AZIONI in griglia da QUATTRO (Davide 2026-09-11: «li voglio
            in ordine in 2 file da 4»). Era un flex-wrap allineato a destra: con
            la colonna larga 200px meno il divisore ne entravano tre, e il
            quarto finiva a capo da solo — file da 3+1, 3+2, mai in colonna.
            In griglia ogni tasto ha il suo posto e le file si allineano da una
            card all'altra. Su telefono resta la fila scorrevole di prima. */}
        <div className="grid grid-cols-4 gap-1 content-start justify-items-center min-w-0 max-md:flex max-md:flex-nowrap max-md:justify-start max-md:overflow-x-auto max-md:pb-1 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden">
          <CardActions
            order={order}
            pickupMailBusy={pickupMailBusy}
            returnBusy={returnBusy}
            onSendPickupMail={onSendPickupMail}
            onCreateReturn={onCreateReturn}
            onShippingSaved={onShippingSaved}
            onOrderToSupplier={onOrderToSupplier}
            orderToSupplierBusy={orderToSupplierBusy}
          />
        </div>
      </div>
    </article>
  );
}
